<template>
  <vl-properties>
    <vl-properties-column mod-full>
      <vl-title tag-name="h3"> {{ $t('product.detail.changeHistory.title') }} </vl-title>
      <div v-if="!loadingEntities && !entities?.length">
        {{ $t('product.detail.changeHistory.noChangeHistory') }}
      </div>
      <div v-if="loadingEntities">
        <vl-loader :message="$t('product.detail.changeHistory.loading')" class="vl-u-display-flex" />
      </div>
      <vl-accordion-list v-if="entities?.length">
        <vl-accordion-list-item v-for="(entity, i) in entities" :id="i" :key="entity.id">
          <vl-accordion
            :id="i.toString()"
            :title="getLabel(entity)"
            :open="opensAutomatically"
            @open="openEntity(entity)"
          >
            <ProductChangeHistoryEntityComponent
              :has-history="entity.hasHistory"
              :loading="isLoading(entity)"
              :failed="hasFailed(entity)"
              :change-history="getChangeHistory(entity)"
            />
          </vl-accordion>
        </vl-accordion-list-item>
      </vl-accordion-list>
    </vl-properties-column>
  </vl-properties>
</template>
<script lang="ts" setup>
import { computed, onUnmounted, watch } from 'vue';
import { ProductChangeHistory, ProductChangeHistoryEntity } from '@/api/frontoffice/clients';
import { useProductChangeHistoryStore } from '@/modules/products/store/product-change-history.store';
import ProductChangeHistoryEntityComponent from './ProductChangeHistoryEntityComponent.vue';

const props = defineProps<{
  runId?: string | null;
}>();

const changeHistoryStore = useProductChangeHistoryStore();

const entities = computed((): ProductChangeHistoryEntity[] => changeHistoryStore.entities);

const loadingEntities = computed((): boolean => changeHistoryStore.loadingEntities);

/** A single entity is not worth collapsing, so its accordion starts open. */
const opensAutomatically = computed((): boolean => entities.value?.length === 1);

const getLabel = (entity: ProductChangeHistoryEntity): string => {
  return `Entiteit ${entity.productEntityCode} - ${entity.productEntityLabel}`;
};

const isLoading = (entity: ProductChangeHistoryEntity): boolean => {
  return changeHistoryStore.entityChangeHistory(entity.id).loading;
};

const hasFailed = (entity: ProductChangeHistoryEntity): boolean => {
  return changeHistoryStore.entityChangeHistory(entity.id).failed;
};

const getChangeHistory = (entity: ProductChangeHistoryEntity): ProductChangeHistory => {
  return changeHistoryStore.entityChangeHistory(entity.id).changeHistory;
};

/** Loads the figures of an entity the first time its accordion is opened. */
const openEntity = async (entity: ProductChangeHistoryEntity): Promise<void> => {
  if (!props.runId || !entity.hasHistory) {
    return;
  }

  await changeHistoryStore.loadChangeHistory(props.runId, entity.id);
};

const loadEntities = async (runId: string): Promise<void> => {
  await changeHistoryStore.getEntities(runId);

  // An accordion that starts open never fires the open event, so load that one here.
  if (opensAutomatically.value) {
    await openEntity(entities.value[0]);
  }
};

watch(
  () => props.runId,
  async (runId) => {
    if (!runId) {
      changeHistoryStore.clear();
      return;
    }

    await loadEntities(runId);
  },
  { immediate: true }
);

onUnmounted(() => {
  changeHistoryStore.clear();
});
</script>
